import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { FaTwitter, FaDownload, FaLink, FaArrowLeft, FaShare } from 'react-icons/fa';
import { SiDiscord } from "react-icons/si";
import ConvertToGif from './ConvertToGif';
import Modal from './Modal';
import '../App.css';

function VideoView() {
  const { videoId } = useParams();
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [conversionStatus, setConversionStatus] = useState({
    converting: false,
    progress: 0,
    status: '',
    error: null,
    gifUrl: null,
    downloadUrl: null
  });

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const response = await axios.get(`https://frogland.onrender.com/video/${videoId}`);
        setVideoData(response.data);
        console.log("Video data:", response.data);
      } catch (error) {
        console.error("Error fetching video:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideo();
  }, [videoId]);

  const handleDownload = () => {
    if (videoId) {
      // Use the server-side download endpoint
      window.location.href = `https://frogland.onrender.com/download/${videoId}`;
    }
  };

  const handleShare = (platform) => {
    const text = "Check out this amazing animation I created with Frogland Animator! 🎨✨";
    const url = window.location.href;
    
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`);
        break;
      case 'discord':
        navigator.clipboard.writeText(url);
        alert('Video URL copied! You can now paste it in Discord');
        break;
      default:
        break;
    }
    
    // Hide share options after selection
    setShowShareOptions(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleConversionStart = () => {
    setIsModalOpen(true);
    setConversionStatus({
      converting: true,
      progress: 0,
      status: 'Starting conversion...',
      error: null,
      gifUrl: null,
      downloadUrl: null
    });
  };

  const handleConversionComplete = ({ gifUrl, downloadUrl }) => {
    setConversionStatus({
      converting: false,
      progress: 100,
      status: 'Conversion complete!',
      error: null,
      gifUrl,
      downloadUrl
    });
  };

  const resetConversion = () => {
    setIsModalOpen(false);
    setConversionStatus({
      converting: false,
      progress: 0,
      status: '',
      error: null,
      gifUrl: null,
      downloadUrl: null
    });
  };

  return (
    <div className="video-view-page">
      <div className="video-view-container">
        <div className="video-view-header">
          <Link to="/" className="back-button">
            <FaArrowLeft /> Back to Home
          </Link>
          <h1>Your Animated Creation</h1>
        </div>
        
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading your animation...</p>
          </div>
        ) : videoData ? (
          <>
            {videoData.instructions && (
              <div className="video-prompt">
                <h3>Animation Prompt:</h3>
                <p>{videoData.instructions || videoData.prompt || 'No prompt information available'}</p>
              </div>
            )}
            
            <div className="video-player-container">
              <video 
                controls 
                autoPlay 
                loop 
                playsInline
                src={videoData.videoUrl}
                className="video-player"
              />
            </div>
            
            <div className="video-actions">
              <div className="primary-actions">
                <button 
                  onClick={handleDownload} 
                  className="action-button primary"
                >
                  <FaDownload /> Download
                </button>
                
                <ConvertToGif 
                  videoUrl={videoData?.videoUrl}
                  onConversionStart={handleConversionStart}
                  onConversionProgress={(progress, status) => setConversionStatus(prev => ({
                    ...prev,
                    progress,
                    status
                  }))}
                  onConversionComplete={handleConversionComplete}
                  onError={(error) => setConversionStatus(prev => ({
                    ...prev,
                    converting: false,
                    error
                  }))}
                />
                
                <div className="share-dropdown">
                  <button 
                    onClick={() => setShowShareOptions(!showShareOptions)} 
                    className="action-button secondary"
                  >
                    <FaShare /> Share
                  </button>
                  
                  {showShareOptions && (
                    <div className="share-options-dropdown">
                      <button 
                        onClick={() => handleShare('twitter')} 
                        className="share-option twitter"
                      >
                        <FaTwitter /> Twitter
                      </button>
                      <button 
                        onClick={() => handleShare('discord')} 
                        className="share-option discord"
                      >
                        <SiDiscord /> Discord
                      </button>
                      <button 
                        onClick={copyToClipboard} 
                        className={`share-option copy ${copySuccess ? 'success' : ''}`}
                      >
                        <FaLink /> {copySuccess ? 'Copied!' : 'Copy Link'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="video-metadata">
                <div className="metadata-item">
                  <span className="metadata-label">Created:</span>
                  <span className="metadata-value">
                    {new Date(videoData.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {videoData.duration && (
                  <div className="metadata-item">
                    <span className="metadata-label">Duration:</span>
                    <span className="metadata-value">{videoData.duration}s</span>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="error-container">
            <p>Video not found or still processing</p>
            <Link to="/" className="action-button primary">
              Return to Home
            </Link>
          </div>
        )}

        <Modal isOpen={isModalOpen} onClose={resetConversion}>
          <div className="gif-conversion-modal">
            <h2>Converting to GIF Loop</h2>
            
            {conversionStatus.converting && (
              <div className="conversion-status">
                <div className="progress-bar">
                  <div 
                    className="progress" 
                    style={{ width: `${conversionStatus.progress}%` }}
                  />
                </div>
                <p>{conversionStatus.status}</p>
              </div>
            )}

            {conversionStatus.error && (
              <div className="error-message">
                <p>Error: {conversionStatus.error}</p>
                <button 
                  onClick={resetConversion}
                  className="action-button secondary"
                >
                  Close
                </button>
              </div>
            )}

            {conversionStatus.gifUrl && (
              <div className="gif-result">
                <img src={conversionStatus.gifUrl} alt="Converted GIF" />
                <div className="gif-actions">
                  <button
                    onClick={() => window.location.href = conversionStatus.downloadUrl}
                    className="action-button primary"
                  >
                    Download GIF
                  </button>
                  <button
                    onClick={resetConversion}
                    className="action-button secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
}

export default VideoView; 